import React, { useState } from 'react';
import {
  FileDown,
  FileText,
  Download,
  Printer,
  Search,
  CheckCircle2,
  FileCode,
  Sparkles,
} from 'lucide-react';
import { DownloadDocument } from '../../types';
import { INITIAL_DOWNLOAD_DOCUMENTS } from '../../data/adminModulesData';

export const AdminDownloadsView: React.FC = () => {
  const [documents, setDocuments] = useState<DownloadDocument[]>(INITIAL_DOWNLOAD_DOCUMENTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [downloadSuccessToast, setDownloadSuccessToast] = useState<string | null>(null);

  const filteredDocs = documents.filter((doc) => {
    const matchesSearch =
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const handleDownload = (doc: DownloadDocument) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, download_count: d.download_count + 1 } : d))
    );
    setDownloadSuccessToast(`กำลังดาวน์โหลด: ${doc.title}`);
    setTimeout(() => setDownloadSuccessToast(null), 3000);
  };

  const handlePrint = (doc: DownloadDocument) => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200">
            <FileDown className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              14. ศูนย์ดาวน์โหลดแบบฟอร์มและเอกสารทางการ (Downloadable Forms & Official Documents)
            </h2>
            <p className="text-xs text-slate-500">
              แบบฟอร์มคำร้องนิติบุคคล, ข้อบังคับหมู่บ้าน, หนังสือมอบฉันทะ และรายงานงบการเงิน
            </p>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {downloadSuccessToast && (
        <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{downloadSuccessToast}</span>
        </div>
      )}

      {/* Search & Filter */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="ค้นหาชื่อแบบฟอร์ม, รหัสเอกสาร..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white"
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="text-xs p-2 rounded-xl border border-slate-200 bg-white font-medium text-slate-700 w-full sm:w-auto"
        >
          <option value="all">ทุกหมวดหมู่ ({documents.length})</option>
          <option value="renovation">คำขอต่อเติมอาคาร</option>
          <option value="debt_free">หนังสือรับรองปลอดหนี้</option>
          <option value="agm_proxy">มอบฉันทะ AGM</option>
          <option value="bylaws">ข้อบังคับนิติบุคคล</option>
          <option value="keycard">บัตรผ่าน & สติ๊กเกอร์</option>
          <option value="financial">รายงานงบการเงิน</option>
        </select>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredDocs.map((doc) => (
          <div
            key={doc.id}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between space-y-4 hover:border-slate-300 transition-all"
          >
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono bg-slate-100 text-slate-700 border border-slate-200">
                  {doc.code}
                </span>

                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 font-mono">
                    {doc.format} • {doc.file_size}
                  </span>
                </div>
              </div>

              <h3 className="font-bold text-slate-900 text-sm leading-snug">{doc.title}</h3>
              
              <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl">
                {doc.description}
              </p>
            </div>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[11px] text-slate-400 font-mono">
                ดาวน์โหลดแล้ว {doc.download_count} ครั้ง • อัปเดต {doc.updated_at}
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePrint(doc)}
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-1 cursor-pointer transition-colors"
                  title="สั่งพิมพ์แบบฟอร์ม"
                >
                  <Printer className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs cursor-pointer transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>ดาวน์โหลดไฟล์</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
